import pako from "pako";
import { runtimeConfig } from "../../config/runtime";
import { HomeMasjid } from "./homeSettings";

export interface IndexMasjid extends HomeMasjid {
  alsoTowns?: string[];
  latitude: number;
  longitude: number;
  // The masjid's YouTube channel link, when it has one.
  youtube?: string;
}

let indexPromise: Promise<IndexMasjid[]> | null = null;

// A .json.gz file from the CDN's jsonfiles container.
export const fetchCdnJson = async (path: string) => {
  await runtimeConfig.initialize();
  const container = runtimeConfig.azureStorageContainerName || "jsonfiles";
  const response = await fetch(`${runtimeConfig.storageUrl}/${container}/${path}`);
  if (!response.ok) throw new Error(`${path} unavailable (${response.status})`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  // The CDN normally sends it already decoded; gunzip only if the raw gzip bytes arrive.
  const gzipped = bytes[0] === 0x1f && bytes[1] === 0x8b;
  return JSON.parse(new TextDecoder().decode(gzipped ? pako.ungzip(bytes) : bytes));
};

// Every active masjid plus the Haramain: the same file the website's search uses. The service
// worker keeps a copy, so once loaded it is searchable offline too.
export const loadMasjidIndex = (): Promise<IndexMasjid[]> => {
  if (!indexPromise) {
    indexPromise = fetchCdnJson("metadata/masjids-all.json.gz").catch((error) => {
      indexPromise = null;
      throw error;
    });
  }
  return indexPromise;
};

const normalise = (text = ""): string =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// People type "mosque" or "masjid" whichever the name uses, so those words alone don't filter.
const GENERIC_WORDS = new Set(["masjid", "masjide", "mosque", "musjid", "the"]);

export const searchMasjids = (
  index: IndexMasjid[],
  query: string,
  limit = 30,
): IndexMasjid[] => {
  const allWords = normalise(query).split(" ").filter(Boolean);
  const words = allWords.filter((w) => !GENERIC_WORDS.has(w));
  const terms = words.length ? words : allWords;
  if (!terms.length) return [];

  return index
    .map((masjid) => {
      const name = normalise(masjid.name);
      const text = `${name} ${normalise([masjid.town, ...(masjid.alsoTowns ?? [])].join(" "))}`;
      if (!terms.every((w) => text.includes(w))) return null;
      // Names that start with the first word, then names that hold every word, then town matches.
      const score = (name.startsWith(terms[0]) ? 0 : 2) + (terms.every((w) => name.includes(w)) ? 0 : 1);
      return { masjid, score };
    })
    .filter((hit): hit is { masjid: IndexMasjid; score: number } => hit !== null)
    .sort((a, b) => a.score - b.score || a.masjid.name.localeCompare(b.masjid.name))
    .slice(0, limit)
    .map((hit) => hit.masjid);
};

const milesBetween = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const rad = (deg: number) => (deg * Math.PI) / 180;
  const a =
    Math.sin(rad(lat2 - lat1) / 2) ** 2 +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lon2 - lon1) / 2) ** 2;
  return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const nearestMasjids = (
  index: IndexMasjid[],
  latitude: number,
  longitude: number,
  limit = 20,
): { masjid: IndexMasjid; miles: number }[] =>
  index
    .map((masjid) => ({
      masjid,
      miles: milesBetween(latitude, longitude, masjid.latitude, masjid.longitude),
    }))
    .sort((a, b) => a.miles - b.miles)
    .slice(0, limit);

export const toHomeMasjid = ({ id, name, town, audio }: IndexMasjid): HomeMasjid => ({
  id,
  name,
  town,
  audio,
});
